/**
 * MockEngineAdapter — deterministic, in-process E1b engine simulation.
 *
 * All runs use the plan from sample-plan.json.
 * startRun() kicks off a timed event sequence (~12 s real time, instant in
 * tests via vi.useFakeTimers() + vi.runAllTimersAsync()).
 */

import type { IEngineAdapter } from './IEngineAdapter.interface'
import type {
  DeployRequest,
  DeploymentGate,
  DeploymentPlan,
  DeploymentRun,
  DeploymentRunSummary,
  EngineMeta,
  EngineEvent,
  EngineCapability,
  EnvironmentStatus,
  DeploymentLayer,
  RunVerificationResult,
} from '../../types/engine.types'

// JSON import — Vite handles this natively
import SAMPLE_PLAN from './sample-plan.json'

// ── Constants ─────────────────────────────────────────────────────────────────

const ENGINE_META: EngineMeta = {
  contractVersion: '1.3.0',
  engineName:      'mock',
  engineVersion:   '0.0.0',
  capabilities: [
    'env-status',
    'adhoc',
    'validate-only',
    'cancel',
    'per-entity-retry',
    'rules-preview',
    'partial-layers',
    'live-logs',
    'approvals',
    'rollback',
  ] satisfies EngineCapability[],
}

// Sample gate added to mock runs
const MOCK_GATE: DeploymentGate = {
  id:                'gate-pre-products',
  layerId:           'products',
  requiredApprovals: 1,
  approvals:         [],
  status:            'pending',
}

// Per-entity record counts for the simulation
const ENTITY_RECORDS: Record<string, number> = {
  AttributeDefinition: 71,
  AttributePicklist:   24,
  Product2:            46,
  PricebookEntry:      48,
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function ts(): string {
  return new Date().toISOString()
}

function uuid(): string {
  return globalThis.crypto.randomUUID()
}

function makePendingRun(
  id:  string,
  req: DeployRequest,
  pipelineId?: string,
): DeploymentRun {
  // Deep-clone the plan layers with pending status
  const layers: DeploymentLayer[] = (SAMPLE_PLAN.layers as DeploymentLayer[]).map((l) => ({
    ...l,
    status:   'pending',
    entities: l.entities.map((e) => ({ ...e, status: 'pending' })),
  }))
  return {
    id,
    status:        'queued',
    mode:          req.mode,
    sourceOrg:     req.sourceOrg,
    targetOrg:     req.targetOrg,
    layers,
    verifications: [],
    totals:        { layers: layers.length, entities: 4, records: 0, errors: 0 },
    gates:         [{ ...MOCK_GATE }],
    ...(pipelineId !== undefined && { pipelineId }),
  }
}

// ── MockEngineAdapter ─────────────────────────────────────────────────────────

export class MockEngineAdapter implements IEngineAdapter {
  private readonly runs    = new Map<string, DeploymentRun>()
  private readonly history: DeploymentRunSummary[] = []
  /** Listeners keyed by runId */
  private readonly listeners = new Map<string, Set<(e: EngineEvent) => void>>()
  /** Env-status listeners keyed by pipelineId */
  private readonly envListeners = new Map<string, Set<(e: EngineEvent) => void>>()

  // ── Meta ───────────────────────────────────────────────────────────────────

  getMeta(): Promise<EngineMeta> {
    return Promise.resolve({ ...ENGINE_META })
  }

  // ── Runs ───────────────────────────────────────────────────────────────────

  listRuns(pipelineId?: string): Promise<DeploymentRunSummary[]> {
    const all = [...this.history]
    const filtered = pipelineId
      ? all.filter((r) => r.id in Object.fromEntries(
          [...this.runs.entries()].filter(([, v]) => v.pipelineId === pipelineId).map(([k]) => [k, true])
        ))
      : all
    return Promise.resolve(filtered.slice().reverse())
  }

  getRun(id: string): Promise<DeploymentRun> {
    const run = this.runs.get(id)
    if (!run) return Promise.reject(new Error(`Run '${id}' not found`))
    return Promise.resolve({ ...run })
  }

  startRun(req: DeployRequest): Promise<DeploymentRun> {
    const id  = uuid()
    const run = makePendingRun(id, req, req.pipelineId)
    this.runs.set(id, run)
    this.history.push(runToSummary(run))

    // Schedule the event sequence asynchronously so the caller receives the
    // queued run first, then events arrive via subscribe().
    setTimeout(() => { this._simulateRun(id) }, 0)

    return Promise.resolve({ ...run })
  }

  cancelRun(id: string): Promise<void> {
    const run = this.runs.get(id)
    if (!run) return Promise.reject(new Error(`Run '${id}' not found`))
    if (run.status === 'succeeded' || run.status === 'failed') {
      return Promise.reject(new Error(`Run '${id}' is already ${run.status}`))
    }
    const cancelled: DeploymentRun = {
      ...run,
      status:      'cancelled',
      completedAt: ts(),
    }
    this.runs.set(id, cancelled)
    this._updateSummary(cancelled)
    return Promise.resolve()
  }

  // ── Plan ───────────────────────────────────────────────────────────────────

  getPlan(_req: DeployRequest): Promise<DeploymentPlan> {
    return Promise.resolve({
      ...(SAMPLE_PLAN as DeploymentPlan),
      gates: [{ ...MOCK_GATE }],
    })
  }

  // ── Run control ────────────────────────────────────────────────────────────

  approveGate(runId: string, gateId: string): Promise<void> {
    const run = this.runs.get(runId)
    if (!run) return Promise.reject(new Error(`Run '${runId}' not found`))
    const gates = (run.gates ?? []).map((g) => {
      if (g.id !== gateId) return g
      const approvals = [...g.approvals, { by: 'operator' }]
      const remaining = Math.max(0, g.requiredApprovals - approvals.length)
      const status    = remaining === 0 ? 'approved' as const : 'pending' as const
      return { ...g, approvals, status }
    })
    const updated = { ...run, gates }
    this.runs.set(runId, updated)
    this._updateSummary(updated)
    const gate      = gates.find((g) => g.id === gateId)
    const remaining = gate ? Math.max(0, gate.requiredApprovals - gate.approvals.length) : 0
    this._emit(runId, {
      type: 'gate.approved', runId, gateId, approvedBy: 'operator', remaining, timestamp: ts(),
    })
    return Promise.resolve()
  }

  retryEntity(runId: string, entityId: string): Promise<void> {
    const run = this.runs.get(runId)
    if (!run) return Promise.reject(new Error(`Run '${runId}' not found`))
    this._emit(runId, { type: 'entity.retrying', runId, entityId, timestamp: ts() })
    const count = ENTITY_RECORDS[entityId] ?? 10
    // Script a minimal re-run for the entity
    setTimeout(() => {
      this._emit(runId, { type: 'entity.started', runId, entityId, timestamp: ts() })
      setTimeout(() => {
        this._emit(runId, { type: 'entity.completed', runId, entityId, recordCount: count, timestamp: ts() })
      }, 500)
    }, 0)
    return Promise.resolve()
  }

  rollback(runId: string, entityId?: string): Promise<void> {
    const run = this.runs.get(runId)
    if (!run) return Promise.reject(new Error(`Run '${runId}' not found`))
    this._emit(runId, {
      type: 'run.rolledback',
      runId,
      timestamp: ts(),
      ...(entityId !== undefined && { entityId }),
    })
    return Promise.resolve()
  }

  // ── Environment status ─────────────────────────────────────────────────────

  getEnvironmentStatuses(_pipelineId: string): Promise<EnvironmentStatus[]> {
    return Promise.resolve([
      { orgAlias: 'arm-source', state: 'idle', deployedBranch: 'main', drift: false },
      { orgAlias: 'arm-target', state: 'idle', drift: true, driftCount: 3 },
    ])
  }

  // ── Subscribe ──────────────────────────────────────────────────────────────

  subscribe(runId: string, onEvent: (e: EngineEvent) => void): () => void {
    if (!this.listeners.has(runId)) this.listeners.set(runId, new Set())
    this.listeners.get(runId)!.add(onEvent)
    return () => { this.listeners.get(runId)?.delete(onEvent) }
  }

  subscribeEnvironments(pipelineId: string, onEvent: (e: EngineEvent) => void): () => void {
    if (!this.envListeners.has(pipelineId)) this.envListeners.set(pipelineId, new Set())
    this.envListeners.get(pipelineId)!.add(onEvent)
    return () => { this.envListeners.get(pipelineId)?.delete(onEvent) }
  }

  // ── Private simulation ─────────────────────────────────────────────────────

  private _emit(runId: string, event: EngineEvent): void {
    this.listeners.get(runId)?.forEach((cb) => cb(event))
  }

  private _updateSummary(run: DeploymentRun): void {
    const idx = this.history.findIndex((s) => s.id === run.id)
    if (idx !== -1) this.history[idx] = runToSummary(run)
  }

  private _patchRun(id: string, patch: Partial<DeploymentRun>): DeploymentRun {
    const current = this.runs.get(id)!
    const next    = { ...current, ...patch }
    this.runs.set(id, next)
    this._updateSummary(next)
    return next
  }

  private _simulateRun(runId: string): void {
    const schedule = (fn: () => void, ms: number): void => {
      setTimeout(fn, ms)
    }

    let cursor = 50

    // run.started
    schedule(() => {
      const run = this._patchRun(runId, { status: 'running', startedAt: ts() })
      this._emit(runId, { type: 'run.started', runId, timestamp: ts() })
      this.runs.set(runId, run)
    }, cursor)

    const plan = SAMPLE_PLAN
    let totalRecords = 0

    for (const layer of plan.layers) {
      const layerBase = cursor

      // layer.started
      schedule(() => {
        this._emit(runId, { type: 'layer.started', runId, layerId: layer.id, timestamp: ts() })
        const run = this.runs.get(runId)!
        const layers = run.layers.map((l) =>
          l.id === layer.id ? { ...l, status: 'running' as const, startedAt: ts() } : l
        )
        this._patchRun(runId, { layers })
      }, layerBase + 100)

      let entityOffset = 200
      for (const entity of layer.entities) {
        const base   = layerBase + entityOffset
        const count  = ENTITY_RECORDS[entity.id] ?? 10

        // entity.started
        schedule(() => {
          this._emit(runId, { type: 'entity.started', runId, entityId: entity.id, timestamp: ts() })
        }, base)

        // entity.progress (halfway)
        schedule(() => {
          this._emit(runId, {
            type: 'entity.progress', runId, entityId: entity.id,
            processed: Math.floor(count / 2), total: count, stage: 'deploying', timestamp: ts(),
          })
        }, base + 400)

        // entity.completed
        schedule(() => {
          totalRecords += count
          this._emit(runId, { type: 'entity.completed', runId, entityId: entity.id, recordCount: count, timestamp: ts() })

          // log line per entity
          this._emit(runId, {
            type: 'log', runId, entityId: entity.id,
            line: `${entity.id}: ${count} records deployed`, timestamp: ts(),
          })
        }, base + 800)

        // verification result
        schedule(() => {
          const result: RunVerificationResult = {
            entityId: entity.id,
            check:    `count ≥ source (${count})`,
            type:     'count',
            status:   'pass',
            expected: count,
            actual:   count,
          }
          this._emit(runId, { type: 'verification.result', runId, result, timestamp: ts() })
        }, base + 900)

        entityOffset += 1200
      }

      // layer.completed
      const layerEnd = layerBase + entityOffset + 100
      schedule(() => {
        this._emit(runId, { type: 'layer.completed', runId, layerId: layer.id, timestamp: ts() })
        const run = this.runs.get(runId)!
        const layers = run.layers.map((l) =>
          l.id === layer.id ? { ...l, status: 'succeeded' as const, completedAt: ts() } : l
        )
        this._patchRun(runId, { layers })
      }, layerEnd)

      cursor = layerEnd + 200
    }

    // run.completed
    schedule(() => {
      const run = this.runs.get(runId)!
      const completed: DeploymentRun = {
        ...run,
        status:      'succeeded',
        completedAt: ts(),
        totals:      { ...run.totals, records: totalRecords },
      }
      this.runs.set(runId, completed)
      this._updateSummary(completed)
      this._emit(runId, { type: 'run.completed', runId, run: completed, timestamp: ts() })
    }, cursor + 300)
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function runToSummary(run: DeploymentRun): DeploymentRunSummary {
  return {
    id:          run.id,
    status:      run.status,
    mode:        run.mode,
    sourceOrg:   run.sourceOrg,
    targetOrg:   run.targetOrg,
    totals:      run.totals,
    ...(run.startedAt   !== undefined && { startedAt:   run.startedAt }),
    ...(run.completedAt !== undefined && { completedAt: run.completedAt }),
  }
}
